class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.setHeaderCartIconAccessibility();
  }

  /**
   * Cart icons are buttons. A click opens this drawer and never navigates to /cart.
   * Capture-phase document delegation keeps working after the icon markup is re-rendered.
   */
  setHeaderCartIconAccessibility() {
    const markIcon = (cartLink) => {
      cartLink.setAttribute('aria-haspopup', 'dialog');
      if (!cartLink.getAttribute('aria-controls')) {
        cartLink.setAttribute('aria-controls', 'CartDrawer');
      }
    };

    document.querySelectorAll('[id^="cart-icon-bubble"]').forEach(markIcon);

    if (CartDrawer.headerCartInterceptBound) return;
    CartDrawer.headerCartInterceptBound = true;

    const cartIconFromEvent = (event) => {
      const target = event.target;
      if (!target || typeof target.closest !== 'function') return null;
      return target.closest('[id^="cart-icon-bubble"]');
    };

    const openDrawerForIcon = (event, cartLink) => {
      event.preventDefault();
      const drawer = document.querySelector('cart-drawer');
      if (!drawer) return;
      markIcon(cartLink);
      drawer.open(cartLink);
    };

    document.addEventListener(
      'click',
      (event) => {
        const cartLink = cartIconFromEvent(event);
        if (!cartLink) return;
        openDrawerForIcon(event, cartLink);
      },
      true
    );

    document.addEventListener(
      'keydown',
      (event) => {
        if (event.code !== 'Space' && event.key !== ' ') return;
        const cartLink = cartIconFromEvent(event);
        if (!cartLink) return;
        openDrawerForIcon(event, cartLink);
      },
      true
    );
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    setTimeout(() => {
      this.classList.add('active');
      this.classList.add('animate');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    if(document.querySelectorAll('.popup-modal.active').length == 0){
      document.body.style.paddingRight = this.getScrollbarWidth() + 'px';
      document.body.classList.add('overflow-hidden');
    }
  }

  close() {
    this.classList.remove('animate');
    setTimeout(() => {
      this.classList.remove('active');
      if(document.querySelectorAll('.popup-modal.active').length == 0){
        document.body.classList.remove('overflow-hidden');
        document.body.style.paddingRight = '';
      }
    }, 500);
    removeTrapFocus(this.activeElement);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.classList.remove('is-empty');
    this.querySelector('.drawer__inner')?.classList.remove('is-empty');
    this.querySelector('cart-drawer-items')?.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionHtml = parsedState.sections?.[section.id];
      if (!sectionHtml) return;
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      const sectionContent = this.getSectionInnerHTML(sectionHtml, section.selector);
      if (sectionElement && sectionContent) sectionElement.innerHTML = sectionContent;
      if(section.id == 'cart-free-delivery' && document.querySelector('#product-page-free-delivery')){
        document.querySelector('#product-page-free-delivery').innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      }
      if(section.id == 'cart-icon-bubble' && document.querySelector('.footer-sticky-nav__item__icon .cart-count-bubble')){
        const parser = new DOMParser();
        const bubble = parser.parseFromString(this.getSectionInnerHTML(parsedState.sections[section.id], section.selector), 'text/html');
        var ib = bubble.querySelector('.cart-count-bubble')?bubble.querySelector('.cart-count-bubble').innerHTML : '' ;
        document.querySelector('.footer-sticky-nav__item__icon .cart-count-bubble').innerHTML = ib;
      }
    });

    setTimeout(() => {
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return '';
    const section = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return section ? section.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
      {
        id: 'cart-icon-bubble--mobile'
      },
      {
        id: 'cart-free-delivery'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
  getScrollbarWidth() {
    return window.innerWidth - document.documentElement.clientWidth;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-icon-bubble--mobile',
        section: 'cart-icon-bubble--mobile',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

class CartDrawerUpsell extends HTMLElement {
  connectedCallback() {
    this.adding = false;
    this.scroller = this.querySelector('[data-upsell-viewport]');
    this.prevBtn = this.querySelector('[data-upsell-prev]');
    this.nextBtn = this.querySelector('[data-upsell-next]');

    this._onPrev = (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.scrollCarousel(-1);
    };
    this._onNext = (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.scrollCarousel(1);
    };
    this._onScroll = () => this.updateNav();
    this._onResize = () => this.updateNav();

    this.prevBtn?.addEventListener('click', this._onPrev);
    this.nextBtn?.addEventListener('click', this._onNext);
    this.scroller?.addEventListener('scroll', this._onScroll, { passive: true });
    window.addEventListener('resize', this._onResize);

    if (this.scroller && typeof ResizeObserver !== 'undefined') {
      this._resizeObserver = new ResizeObserver(() => this.updateNav());
      this._resizeObserver.observe(this.scroller);
    }

    // Start flush left; refresh nav after layout (drawer may open later)
    if (this.scroller) this.scroller.scrollLeft = 0;
    requestAnimationFrame(() => this.updateNav());
    window.setTimeout(() => this.updateNav(), 100);

    const drawer = this.closest('cart-drawer');
    if (drawer) {
      this._drawerObserver = new MutationObserver(() => {
        if (drawer.classList.contains('active') || drawer.classList.contains('animate')) {
          if (this.scroller && this.scroller.scrollLeft < 0) this.scroller.scrollLeft = 0;
          this.updateNav();
        }
      });
      this._drawerObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });
    }

    this.querySelectorAll('[data-upsell-variant]').forEach((swatch) => {
      swatch.addEventListener('click', () => {
        const slide = swatch.closest('[data-upsell-slide]');
        if (!slide) return;
        slide.querySelectorAll('[data-upsell-variant]').forEach((item) => {
          item.classList.toggle('is-selected', item === swatch);
          item.setAttribute('aria-pressed', item === swatch ? 'true' : 'false');
        });
        const button = slide.querySelector('[data-upsell-add]');
        if (button) button.dataset.variantId = swatch.dataset.variantId;
      });
    });
    this.querySelectorAll('[data-upsell-add]').forEach((button) => {
      button.addEventListener('click', (event) => this.add(event.currentTarget));
    });
  }

  disconnectedCallback() {
    this.prevBtn?.removeEventListener('click', this._onPrev);
    this.nextBtn?.removeEventListener('click', this._onNext);
    this.scroller?.removeEventListener('scroll', this._onScroll);
    if (this._onResize) window.removeEventListener('resize', this._onResize);
    this._resizeObserver?.disconnect();
    this._drawerObserver?.disconnect();
  }

  scrollCarousel(direction) {
    const scroller = this.scroller || this.querySelector('[data-upsell-viewport]');
    if (!scroller) return;

    const slide = this.querySelector('[data-upsell-slide]');
    const gap = 6;
    const cardWidth = slide ? slide.offsetWidth + gap : 78;
    const delta = direction * cardWidth * 2;
    const maxScroll = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
    const nextLeft = Math.max(0, Math.min(maxScroll, scroller.scrollLeft + delta));

    if (typeof scroller.scrollTo === 'function') {
      scroller.scrollTo({ left: nextLeft, behavior: 'smooth' });
    } else {
      scroller.scrollLeft = nextLeft;
    }
    window.setTimeout(() => this.updateNav(), 350);
  }

  updateNav() {
    const scroller = this.scroller || this.querySelector('[data-upsell-viewport]');
    const prevBtn = this.prevBtn || this.querySelector('[data-upsell-prev]');
    const nextBtn = this.nextBtn || this.querySelector('[data-upsell-next]');
    if (!scroller) return;

    const maxScroll = scroller.scrollWidth - scroller.clientWidth;
    const atStart = scroller.scrollLeft <= 2;
    const atEnd = scroller.scrollLeft >= maxScroll - 2;
    const canScroll = maxScroll > 4;

    if (prevBtn) {
      prevBtn.disabled = !canScroll || atStart;
      prevBtn.setAttribute('aria-disabled', String(!canScroll || atStart));
      prevBtn.toggleAttribute('hidden', !canScroll);
    }
    if (nextBtn) {
      nextBtn.disabled = !canScroll || atEnd;
      nextBtn.setAttribute('aria-disabled', String(!canScroll || atEnd));
      nextBtn.toggleAttribute('hidden', !canScroll);
    }
  }

  add(button) {
    if (this.adding || !button) return;
    const variantId = Number(button.dataset.variantId);
    if (!variantId) return;

    const cart = this.closest('cart-drawer');
    const slide = button.closest('[data-upsell-slide]');
    this.adding = true;
    button.classList.add('is-loading');
    button.setAttribute('aria-disabled', 'true');
    slide?.classList.add('is-adding');

    const body = {
      id: variantId,
      quantity: 1,
    };
    if (cart) {
      body.sections = cart.getSectionsToRender().map((section) => section.id);
      body.sections_url = window.location.pathname;
    }

    fetch(`${routes.cart_add_url}`, { ...fetchConfig('javascript'), body: JSON.stringify(body) })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          const errors = document.getElementById('CartDrawer-CartErrors');
          if (errors) errors.textContent = response.description || response.message || window.cartStrings.error;
          slide?.classList.remove('is-adding');
          return;
        }

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-drawer-upsell',
          productVariantId: variantId,
          cartData: response,
        });

        if (cart) {
          cart.classList.remove('is-empty');
          cart.renderContents(response);
        }
      })
      .catch((error) => {
        console.error(error);
        slide?.classList.remove('is-adding');
      })
      .finally(() => {
        this.adding = false;
        button.classList.remove('is-loading');
        button.removeAttribute('aria-disabled');
      });
  }
}

if (!customElements.get('cart-drawer-upsell')) {
  customElements.define('cart-drawer-upsell', CartDrawerUpsell);
}